import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie } from "lucide-react";

const KEY = "orizon360_cookie_consent";
const SIX_MONTHS = 1000 * 60 * 60 * 24 * 182;

export const readConsent = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.date > SIX_MONTHS) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const CookieBanner = ({ open, onClose }) => {
  const [custom, setCustom] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  const save = (prefs) => {
    localStorage.setItem(KEY, JSON.stringify({ ...prefs, date: Date.now() }));
    setCustom(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-live="polite"
          aria-label="Gestion des cookies"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[90] rounded-2xl border border-white/10 bg-slate/95 backdrop-blur-xl text-white p-6 shadow-2xl"
          data-testid="cookie-banner"
        >
          <div className="flex items-start gap-3">
            <Cookie className="text-copper shrink-0 mt-0.5" size={22} />
            <div>
              <h2 className="font-heading uppercase text-xl font-bold">Vos cookies, votre choix</h2>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">
                Ce site utilise uniquement des cookies strictement nécessaires à son fonctionnement. Les cookies de mesure d'audience ne sont déposés qu'avec votre accord.{" "}
                <Link to="/politique-de-confidentialite" className="underline underline-offset-2 text-white hover:text-copper">En savoir plus</Link>
              </p>
            </div>
          </div>

          {custom && (
            <div className="mt-4 space-y-3 rounded-xl bg-white/5 p-4" data-testid="cookie-custom-panel">
              <label className="flex items-center justify-between gap-4 text-sm">
                <span>Cookies nécessaires <span className="text-white/50">(toujours actifs)</span></span>
                <input type="checkbox" checked disabled className="accent-copper" />
              </label>
              <label className="flex items-center justify-between gap-4 text-sm">
                <span>Mesure d'audience</span>
                <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="accent-copper w-4 h-4" data-testid="cookie-analytics-checkbox" />
              </label>
            </div>
          )}

          <div className="mt-5 grid grid-cols-3 gap-2">
            <button onClick={() => save({ necessary: true, analytics: false })} className="btn !px-3 !py-2.5 !text-sm border-2 border-white/30 hover:bg-white hover:text-slate" data-testid="cookie-refuse-button">Refuser</button>
            {custom ? (
              <button onClick={() => save({ necessary: true, analytics })} className="btn !px-3 !py-2.5 !text-sm border-2 border-white/30 hover:bg-white hover:text-slate" data-testid="cookie-save-button">Enregistrer</button>
            ) : (
              <button onClick={() => setCustom(true)} className="btn !px-3 !py-2.5 !text-sm border-2 border-white/30 hover:bg-white hover:text-slate" data-testid="cookie-customize-button">Personnaliser</button>
            )}
            <button onClick={() => save({ necessary: true, analytics: true })} className="btn !px-3 !py-2.5 !text-sm bg-copper hover:bg-copper-dark" data-testid="cookie-accept-button">Accepter</button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
